import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import type { ActionState } from "./types";
import styles from "./ActionCard.module.css";

interface ActionCardProps {
  id: string;
  title: string;
  body?: string;
  state: ActionState;
  actMs: number;
  onComplete: (id: string) => void;
  onOverride: (id: string) => void;
}

export function ActionCard({
  id,
  title,
  body,
  state,
  actMs,
  onComplete,
  onOverride,
}: ActionCardProps) {
  const reduced = useReducedMotion();
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startedAt = useRef<number | null>(null);

  // countdown -> complete when state === "pending"
  useEffect(() => {
    if (state !== "pending") return;
    if (reduced) {
      setProgress(1);
      const t = setTimeout(() => onComplete(id), 120);
      return () => clearTimeout(t);
    }
    startedAt.current = performance.now();
    const tick = (now: number) => {
      if (startedAt.current === null) return;
      const p = Math.min(1, (now - startedAt.current) / actMs);
      setProgress(p);
      if (p >= 1) {
        onComplete(id);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [id, state, actMs, reduced, onComplete]);

  // collapse done / overridden cards shortly after the state flip
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    if (state === "pending") return;
    const t = setTimeout(() => setCollapsed(true), 420);
    return () => clearTimeout(t);
  }, [state]);

  if (collapsed) return null;

  const flashingDone = state === "done";
  const overriddenClass = state === "overridden" ? styles.overridden : "";
  const remaining = state === "pending" ? Math.max(0, Math.ceil(((1 - progress) * actMs) / 1000)) : 0;

  return (
    <div className={`${styles.card} ${flashingDone ? styles.flashDone : ""} ${overriddenClass}`}>
      <div className={styles.row}>
        <span className={styles.glyph} aria-hidden>
          ◆
        </span>
        <span className={styles.actingLabel}>
          {state === "pending" && "ripper is applying"}
          {state === "done" && "applied"}
          {state === "overridden" && "held — waiting for your call"}
        </span>
        <span className={styles.title}>{title}</span>
        {state === "pending" && (
          <button
            type="button"
            className={styles.override}
            onClick={() => onOverride(id)}
            title="hold — review before applying"
          >
            override · {remaining}s
          </button>
        )}
      </div>
      {body && <div className={styles.body}>{body}</div>}
      {state === "pending" && (
        <div className={styles.progressWrap} aria-hidden>
          <div
            className={styles.progressFill}
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      )}
    </div>
  );
}
